/**
 * Generic CRM list page for custom entity types.
 *
 * Resolves the entity type from the URL slug (lowercase plural label)
 * and renders a CRMObjectListPage with a minimal config built from
 * the schema API.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { QueryErrorState } from '@/lib/components';
import { createCrmObject } from '@/lib/crm/core/createObject';
import type { ListPageConfig } from '@/lib/crm/core/types';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import {
  fetchPropertySchemas,
  propertySchemaQueryKey,
} from '@/lib/crm/hooks/usePropertySchema';
import { entityTypeHasPipeline } from '@/lib/crm/hooks/useStages';
import { CRMObjectListPage } from '@/lib/crm/list';
import { buildPipelineScopeFilterConditions } from '@/lib/crm/list/utils/pipelineScopeFilter';
import { getMemoAssociationNames } from '@/lib/crm/record/memo/notes.utils';
import {
  associationMatchesSource,
  buildEntityKindMap,
  getEntityKinds,
} from '@/lib/crm/utils/associationMatching';
import { toSnakeCase } from '@/lib/crm/utils/entityTypeUtils';
import {
  registerEntityTypeIcons,
  registerEntityTypeKinds,
} from '@/lib/crm/utils/icons';
import { logger } from '@/lib/logger';
import {
  useViewHealthErrorMarker,
  useViewHealthMarker,
} from '@/lib/view-health';
import NotFoundPage from '@/pages/NotFoundPage';

import { GenericCrmListSkeleton } from './GenericCrmListSkeleton';

export default function GenericCrmList() {
  // Both `crm/:entitySlug` and `pipeline/:entitySlug/:pipelineId` mount this
  // component. When `pipelineId` is present we're on the pipeline route — switch
  // the default view from table to pipeline (kanban) so the user sees stage
  // columns immediately. Generic across all pipelineable entity types.
  const { entitySlug, pipelineId } = useParams<{
    entitySlug: string;
    pipelineId?: string;
  }>();
  const { currentOrg } = useOrg();
  const user = useUser();
  const orgId = currentOrg?.id;
  const queryClient = useQueryClient();

  const {
    associationTypes: allAssociationTypes,
    isLoading: associationTypesLoading,
    error: associationTypesError,
  } = useAssociationTypes(orgId);

  // Content signature so the inner queryFn re-runs when association-type
  // contents change — not just when the count changes. Keying on
  // `allAssociationTypes.length` alone would leave baseConfig stale after a
  // rename, cardinality change, value_write_access change, or any in-place
  // edit that preserves count. value_write_access is included because the
  // queryFn filters on it to build createAssociations — a permission flip
  // must invalidate this query.
  const associationTypesSignature = useMemo(
    () =>
      allAssociationTypes
        .map(a => {
          // Include kind-based fields AND their exclusion lists so any schema
          // change that affects kind-aware matching invalidates the cache —
          // not just a flip between type-based and kind-based. Without the
          // exclusion lists, an exclusion-only schema edit (e.g. adding
          // `ahoy_user` to `source_excluded_entity_type_names`) would not
          // invalidate baseConfig, leaving `createAssociations` stale.
          const sourceExcluded = (a.source_excluded_entity_type_names ?? [])
            .slice()
            .sort()
            .join(',');
          const targetExcluded = (a.target_excluded_entity_type_names ?? [])
            .slice()
            .sort()
            .join(',');
          return `${a.association_type_name}:${a.source_entity_type_name}:${a.target_entity_type_name}:${a.source_entity_kind_name ?? ''}:${sourceExcluded}:${a.target_entity_kind_name ?? ''}:${targetExcluded}:${a.cardinality}:${a.value_write_access}`;
        })
        .sort()
        .join('|'),
    [allAssociationTypes]
  );

  // Fetch object types and find the one matching the URL slug
  //
  // Use `isPending` (no data yet, not errored) rather than `isLoading`
  // (`isPending && isFetching`) to gate the loading branches below. This
  // query is disabled until association types resolve (see `enabled`
  // below); when that flag flips true, TanStack Query schedules the fetch
  // in an effect that runs *after* the render commits, so there's a render
  // where `enabled` is true but `isFetching` hasn't started yet. In that
  // render `isLoading` is false even though `baseConfig` is still
  // undefined, which briefly fell through to the not-found branch below
  // instead of the skeleton.
  const {
    data: baseConfig,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'objectTypes',
      orgId,
      'resolveSlug',
      entitySlug,
      // Distinguish /crm/:slug from /pipeline/:slug/:id so the config rebuild
      // (notably defaultView) fires when the user switches between the two
      // routes. Using a route-mode discriminator instead of the raw
      // pipelineId avoids one cache entry per pipeline — the resolved config
      // is identical for every pipeline route on the same slug.
      pipelineId ? 'pipeline-route' : 'crm-route',
      associationTypesSignature,
    ],
    queryFn: async (): Promise<ListPageConfig | null> => {
      if (!orgId || !entitySlug) {
        return null;
      }
      // Fetch through the CANONICAL object-types cache entry
      // (entityTypesQueryKey) instead of an imperative SchemaClient call.
      // SideNavigation (always mounted) fetches the same list under this key,
      // so on any warm session this resolves from cache/in-flight dedup —
      // previously the bespoke fetch here re-hit the endpoint serially and
      // added a full round-trip to every board/list navigation.
      const objectTypes = await queryClient.fetchQuery({
        queryKey: entityTypesQueryKey(orgId),
        queryFn: () => fetchEntityTypeSchemas(orgId),
        staleTime: 5 * 60 * 1000,
      });
      // Register all entity type icons so avatars resolve correctly
      registerEntityTypeIcons(
        Object.fromEntries(
          objectTypes
            .filter(t => t.icon)
            .map(t => [t.entity_type_name, t.icon ?? ''])
        )
      );
      registerEntityTypeKinds(
        Object.fromEntries(
          objectTypes.map(t => [t.entity_type_name, t.entity_kind ?? []])
        )
      );
      const resolvedName = toSnakeCase(entitySlug ?? '');
      const match = objectTypes.find(t => t.entity_type_name === resolvedName);
      if (!match) {
        return null;
      }

      // Build set of stage entity type names using entity_kind from schema
      const stageTypeNames = new Set<string>(
        objectTypes
          .filter(ot => ot.entity_kind.includes('stage'))
          .map(ot => ot.entity_type_name)
      );

      // Filter the shared association types list to those where this entity is
      // the source. Avoids a redundant network request — the full list is already
      // fetched by useAssociationTypes and shared via the React Query cache.
      //
      // Uses kind-aware matching so kind-based associations like
      // ahoy_contact_to_note (source_entity_kind_name=person) are included —
      // a plain `source_entity_type_name === ...` check silently dropped them.
      const entityKindMap = buildEntityKindMap(objectTypes);
      const sourceKinds = getEntityKinds(match.entity_type_name, entityKindMap);
      const associationTypes = allAssociationTypes.filter(a =>
        associationMatchesSource(a, match.entity_type_name, sourceKinds)
      );

      // Check if entity type is pipelineable via the declarative kind rule.
      const hasPipeline = entityTypeHasPipeline(
        match.entity_type_name,
        objectTypes
      );

      let stageAssociationTypeName: string | undefined;

      // Only set stageAssociationTypeName when entity is structurally pipelineable.
      // CRMObjectListPage routes to PipelinePage based solely on this field's presence;
      // setting it for stage-only entities (e.g., contacts) would incorrectly route them.
      if (hasPipeline) {
        const stageAssociation = associationTypes.find(
          a =>
            stageTypeNames.has(a.target_entity_type_name) &&
            a.cardinality === 'many_to_one'
        );

        if (stageAssociation) {
          stageAssociationTypeName = stageAssociation.association_type_name;
        }
      }

      // Collect user-defined associations for the create form.
      // Exclude pipeline infrastructure associations (stage, pipeline types)
      // which are handled automatically. Use entity_kind from the schema
      // rather than brittle name suffix checks.
      const kindByName = new Map(
        objectTypes.map(t => [t.entity_type_name, t.entity_kind])
      );
      const userAssociationNames = associationTypes
        .filter(a => {
          // Skip stage association — handled separately for pipelineable entities
          if (a.association_type_name === stageAssociationTypeName) {
            return false;
          }
          // Skip associations targeting pipeline/stage infrastructure types
          const targetKinds = kindByName.get(a.target_entity_type_name) ?? [];
          if (
            targetKinds.includes('pipeline') ||
            targetKinds.includes('stage')
          ) {
            return false;
          }
          // Skip note associations — notes are managed via the Notes tab, not the create form
          if (a.target_entity_type_name === 'ahoy_note') {
            return false;
          }
          // Only include writable associations in the create form
          return a.value_write_access === 'all';
        })
        .map(a => a.association_type_name);

      // Add custom empty state for custom entities
      const emptyStateComponent = match.entity_type_name.startsWith('org_')
        ? (props: { onCreateClick: () => void }) => (
            <CustomEntityEmptyState
              displayName={match.label}
              pluralName={match.label_plural}
              onCreateClick={props.onCreateClick}
            />
          )
        : undefined;

      // Fetch custom property types so they appear in the create form.
      //
      // Filter on `entity_id != null` rather than the `org_` name prefix:
      // an entity_id is set when the property type was created as an
      // ``ahoy_property_type`` event, which covers both regular user-defined
      // ``org_*`` properties AND kind-stamped overrides that carry an
      // ``ahoy_*`` name (e.g. a custom calendar entity that opts in to
      // ``ahoy_start_date`` with a renamed label). Pure kind-stamped
      // templates with no override have `entity_id` null and are
      // intentionally excluded so they don't clutter the create form.
      // Fetch through the CANONICAL property-schema cache entry so the list
      // page / PipelinePage's usePropertySchema (same key) cache-hits at
      // mount instead of re-fetching the identical schema seconds later.
      const propertyTypes = await queryClient.fetchQuery({
        queryKey: propertySchemaQueryKey(orgId, match.entity_type_name),
        queryFn: () => fetchPropertySchemas(orgId, match.entity_type_name),
        staleTime: 5 * 60 * 1000,
      });
      const customPropertyNames = propertyTypes
        // Columns are read-only display — every user-writable custom property
        // gets a column regardless of field type. The "no broken editor for
        // json" concern is an *edit*-time gate (deriveCreatePropertyNames for
        // the create form; isPropertyEditable for inline cells), not a reason
        // to hide the column entirely.
        .filter(p => p.entity_id != null && p.value_write_access === 'all')
        .map(p => p.property_type_name);

      const baseColumns: ListPageConfig['columns'] = [
        { kind: 'object_name', sortable: true, width: 280 },
        ...(customPropertyNames.length > 0
          ? customPropertyNames.map(name => ({
              kind: 'property' as const,
              name,
            }))
          : []),
        { kind: 'property', name: 'ahoy_updated_at' },
      ];

      const isNote = match.entity_type_name === 'ahoy_note';
      const isTask = match.entity_type_name === 'ahoy_task';

      // Note/task-specific columns. `ahoy_date` (label "Due") is a built-in
      // task-only property — notes have no due date; the stage and owner are
      // associations (labelled "Status" / "Owner" in the schema); tasks also
      // lead with their pipeline association. None of these is picked up by
      // the customPropertyNames filter above (a built-in property has a null
      // entity_id, and associations aren't property types at all). We still
      // filter `ahoy_date` out of customPropertyNames in case an org adds a
      // writable override with the same name, so it doesn't produce a
      // duplicate column. The previous start-date / start-time columns were
      // removed: those are event-kind properties and neither type is an
      // `event`-kind entity, so they never existed on the schema.
      const memoDatePropertyName = 'ahoy_date';
      const customMemoColumns = customPropertyNames
        .filter(name => name !== memoDatePropertyName)
        .map(name => ({
          kind: 'property' as const,
          name,
        }));

      let columns: ListPageConfig['columns'] = baseColumns;
      if (isNote) {
        const names = getMemoAssociationNames('ahoy_note');
        columns = [
          { kind: 'object_name', sortable: true, width: 280 },
          { kind: 'association', name: names.owner },
          ...customMemoColumns,
          { kind: 'property', name: 'ahoy_updated_at' },
        ];
      } else if (isTask) {
        // Tasks: pipeline · stage · due date · assignee (owner), in that
        // order — the board's grouping first, then the work itself.
        const names = getMemoAssociationNames('ahoy_task');
        // The task family always has real pipeline/stage association names
        // (unlike notes, which have none — epic #13640); the `? [...] : []`
        // spreads narrow the nullable type without changing behavior.
        columns = [
          { kind: 'object_name', sortable: true, width: 280 },
          ...(names.pipeline
            ? [{ kind: 'association', name: names.pipeline } as const]
            : []),
          ...(names.stage
            ? [{ kind: 'association', name: names.stage } as const]
            : []),
          { kind: 'property', name: memoDatePropertyName },
          { kind: 'association', name: names.owner },
          ...customMemoColumns,
          { kind: 'property', name: 'ahoy_updated_at' },
        ];
      }

      // Notes' list page heading uses the schema's plural label ("Notes")
      // like every other entity type. The previous "Notes & Tasks" framing
      // is gone — staged notes are still notes, just notes that happen to
      // be in a pipeline column.
      const pluralTitle = match.label_plural;

      // Default view: when we're on the pipeline route (pipelineId present in
      // URL params), force `pipeline` (kanban) regardless of entity. Otherwise
      // notes get `table` (their preferred default); all other types fall
      // through to the CRMObjectListPage default which is pipeline when a stage
      // association exists, else table.
      let defaultView: 'table' | 'pipeline' | undefined;
      if (pipelineId) {
        defaultView = 'pipeline';
      } else if (isNote) {
        defaultView = 'table';
      }

      return {
        entityType: match.entity_type_name as ListPageConfig['entityType'],
        title: match.label,
        pluralTitle,
        icon: match.icon,
        columns,
        createProperties: ['ahoy_display_name', ...customPropertyNames],
        createAssociations: [
          ...(stageAssociationTypeName ? [stageAssociationTypeName] : []),
          ...userAssociationNames,
        ],
        metrics: [],
        defaultFilters: {
          sort: 'ahoy_display_name',
          order: 'asc',
          page: 1,
          page_size: 20,
        },
        showTimeFilter: false,
        emptyStateComponent,
        stageAssociationTypeName,
        ...(defaultView ? { defaultView } : {}),
      };
    },
    enabled:
      !!orgId &&
      !!entitySlug &&
      !associationTypesLoading &&
      !associationTypesError,
  });

  // Get pipelines data for default stage assignment
  // Only fetch when config is loaded and entity is pipelineable
  const { pipelines } = usePipelines(
    baseConfig?.stageAssociationTypeName ? orgId : undefined,
    baseConfig?.stageAssociationTypeName ? baseConfig.entityType : undefined
  );

  const handleCreateObject = useCallback(
    async (orgId: number, data: Record<string, unknown>) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      if (!baseConfig) {
        throw new Error('Config not loaded');
      }

      // Build default stage association for pipelineable entities
      let defaultStageAssoc;
      if (baseConfig.stageAssociationTypeName) {
        try {
          // Only attempt default stage assignment if pipelines exist
          if (pipelines.length > 0) {
            const defaultPipeline =
              pipelines.find(p => p.is_default) ?? pipelines[0];
            // Sort stages by order to ensure we get the first stage in the pipeline
            const sortedStages = [...(defaultPipeline?.stages ?? [])].sort(
              (a, b) => a.properties.order - b.properties.order
            );
            const defaultStage = sortedStages[0];
            if (defaultStage?.entity_id) {
              defaultStageAssoc = {
                association_type_name: baseConfig.stageAssociationTypeName,
                target_entity_id: defaultStage.entity_id,
              };
            }
          }
        } catch (err) {
          logger.error(
            'Failed to resolve default stage',
            err instanceof Error ? err : new Error(String(err)),
            {
              component: 'generic-crm-list',
              entityType: baseConfig.entityType,
              orgId,
              userId: user?.id,
            }
          );
        }
      }

      await createCrmObject(
        orgId,
        baseConfig.entityType,
        data as Record<string, unknown>,
        user.id,
        defaultStageAssoc
          ? { extraAssociations: [defaultStageAssoc] }
          : undefined
      );
    },
    [baseConfig, pipelines, user?.id]
  );

  // Inject the pipeline-scope filter downstream of the cached baseConfig
  // (NOT into the query cache key, which deliberately collapses all pipeline
  // routes for a slug into one entry — see the objectTypes query key above).
  // Only affects the TABLE view: PipelinePage receives explicit props and
  // never reads config.defaultFilterConditions, so the kanban view is
  // unaffected. Mirrors MeetingTypesPage's downstream-injection pattern.
  const config = useMemo<ListPageConfig | null>(() => {
    if (!baseConfig) {
      return null;
    }
    const pipelineScopeConditions = buildPipelineScopeFilterConditions(
      baseConfig.stageAssociationTypeName,
      pipelineId
    );
    if (pipelineScopeConditions.length === 0) {
      return baseConfig;
    }
    return { ...baseConfig, defaultFilterConditions: pipelineScopeConditions };
  }, [baseConfig, pipelineId]);

  useViewHealthMarker('crm-entity-list-ready', {
    ready: !isPending && !!config,
  });
  useViewHealthErrorMarker('crm-entity-type-not-found', {
    triggered:
      !!orgId &&
      !!entitySlug &&
      !isPending &&
      !isError &&
      !associationTypesError &&
      !associationTypesLoading &&
      config === null,
  });

  // Show skeleton while query is loading OR disabled (waiting for org/slug/association types to resolve)
  if (isPending || associationTypesLoading || !orgId || !entitySlug) {
    return <GenericCrmListSkeleton />;
  }

  if ((isError || associationTypesError) && !baseConfig) {
    return (
      <div className='flex items-center justify-center flex-1 min-h-0 h-full'>
        <QueryErrorState
          error={error ?? associationTypesError}
          label="We couldn't load your configuration"
          size='lg'
        />
      </div>
    );
  }

  // Unknown slug (e.g. a stale link or a typo): render the same not-found page
  // as the dashboard `*` catch-all so the user gets an explanation and a way
  // back, rather than a bare sentence in the content area.
  if (!config) {
    return <NotFoundPage />;
  }

  // Keyed by route identity (entity type + which pipeline, if any) so React
  // remounts CRMObjectListPage on navigation instead of reusing the same
  // instance. Without this, navigating e.g. from the Tasks pipeline's Table
  // view to Notes keeps CRMObjectListPage mounted across the entitySlug
  // change — its filter state is seeded once via a lazy useState
  // (readInitialListUrlState in crm-object-list-page.tsx) that only re-reads
  // the URL on a genuine mount, so the previous entity type's filters (e.g.
  // "Tasks pipeline") stayed applied to the new list. A key reset is the
  // React 19 pattern for this — see docs/claude/FRONTEND_PATTERNS.md
  // "No prop-to-state sync effects" / REACT_19_PATTERNS.md.
  return (
    <CRMObjectListPage
      key={`${entitySlug}:${pipelineId ?? ''}`}
      config={config}
      onCreateObject={handleCreateObject}
    />
  );
}

/**
 * Custom empty state component for custom entities.
 */
function CustomEntityEmptyState({
  displayName,
  pluralName,
  onCreateClick,
}: {
  displayName: string;
  pluralName: string;
  onCreateClick: () => void;
}) {
  return (
    <div className='max-w-sm mx-auto text-center py-20'>
      <div className='w-12 h-12 rounded-full border-8 border-neutral-50 bg-neutral-100 flex items-center justify-center mx-auto mb-4'>
        <svg
          className='w-6 h-6 text-neutral-600'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
          strokeWidth={2}
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
          />
        </svg>
      </div>
      <h3 className='text-lg font-semibold text-neutral-900 mb-1'>
        No {pluralName.toLowerCase()} yet
      </h3>
      <p className='text-sm text-neutral-600 mb-6'>
        Create your first {displayName.toLowerCase()} to get started.
      </p>
      <Button
        color='primary'
        size='md'
        iconLeading={<PlusCircle className='w-5 h-5' />}
        onClick={onCreateClick}
      >
        Create {displayName.toLowerCase()}
      </Button>
    </div>
  );
}
